import { useState } from "react";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { MinusIcon, PlusIcon } from "@heroicons/react/24/solid";
import { useAppSelector } from "../../../app/hooks";
import { UserRole } from "../../../app/userSlice";
import Avatar from "../../../components/Avatar";
import {
	PeerReviewViewModel,
	usePeerReviewMarkAsReadMutation,
} from "../../peerReviews/peerReviewsApiSlice";
import { PeerReviewInteraction } from "../../peerReviews/PeerReviewInteraction";

interface PeerReviewsProps {
	draftId: number;
	reviews: PeerReviewViewModel[];
	expandAllByDefault?: boolean;
}

export default function PeerReviews(props: PeerReviewsProps) {
	return (
		<section>
			<h1>Peer Reviews</h1>
			{props.reviews.map((review) => {
				return (
					<PeerReviewAccordion
						key={review.id}
						draftId={props.draftId}
						review={review}
						expandByDefault={props.expandAllByDefault ?? false}
					/>
				);
			})}
		</section>
	);
}

interface PeerReviewAccordionProps {
	draftId: number;
	review: PeerReviewViewModel;
	expandByDefault: boolean;
}

function PeerReviewAccordion(props: PeerReviewAccordionProps) {
	const [isViewed, setIsViewed] = useState(false);
	const [callMarkReviewAsRead] = usePeerReviewMarkAsReadMutation();

	function handleClick(review: PeerReviewViewModel, open: boolean) {
		if (!open && !isViewed) {
			callMarkReviewAsRead({ id: review.id });
			setIsViewed(true);
		}
	}

	return (
		<Disclosure
			as="div"
			key={props.review.id}
			className="pt-6"
			defaultOpen={props.expandByDefault}>
			{({ open }) => (
				<>
					<dt>
						<DisclosureButton
							className={`flex w-full items-start justify-between text-left text-gray-900 p-4 border border-gray-200 bg-gray-50 rounded-md
								${open ? "border-b-0 rounded-b-none" : ""}`}
							onClick={() => handleClick(props.review, open)}>
							<PeerReviewAccordionHeader review={props.review} />
							<span className="ml-6 flex h-7 items-center">
								{open ? (
									<MinusIcon className="h-5 w-5" aria-hidden="true" />
								) : (
									<PlusIcon className="h-5 w-5" aria-hidden="true" />
								)}
							</span>
						</DisclosureButton>
					</dt>

					<PeerReviewAccordionBody
						className="border border-gray-200 border-t-0 rounded-b-md"
						open={open}
						draftId={props.draftId}
						review={props.review}
					/>
				</>
			)}
		</Disclosure>
	);
}

interface PeerReviewHeaderProps {
	review: PeerReviewViewModel;
}

function PeerReviewAccordionHeader({ review }: PeerReviewHeaderProps) {
	return (
		<div className="flex space-x-4 items-center">
			<div>by {review.reviewer}</div>
			<Avatar
				userId={review.reviewerId}
				initials={review.reviewerInitials ?? "?"}
				hasImage={review.reviewerHasImage}
				mini={true}
			/>
		</div>
	);
}

interface PeerReviewBodyProps {
	className?: string;
	open: boolean;
	draftId: number;
	review: PeerReviewViewModel;
}

function PeerReviewAccordionBody(props: PeerReviewBodyProps) {
	const role = useAppSelector((state) => state.user.role);
	return (
		<DisclosurePanel as="dd" className={`${props.className ?? ""} pr-12 p-4`}>
			<PeerReviewInteraction
				draftId={props.draftId}
				review={props.review}
				readOnly={role === UserRole.Proctor}
			/>
		</DisclosurePanel>
	);
}
